import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { CloudRain, Sun, CloudFog, Warning, Clock, MapPin, ArrowDown } from "@phosphor-icons/react";
import { getCityPulse } from "@/lib/api";

// Tier order — used both for grouping AND for primary sort.
const TIER_ORDER = { downpour: 0, drizzling: 1, incoming: 2, dry: 3 };

const STATUS_META = {
  downpour: {
    label: "Downpour",
    color: "#dc2626",     // red-600 — danger
    soft: "rgba(220,38,38,0.10)",
    Icon: CloudRain,
  },
  drizzling: {
    label: "Drizzling",
    color: "#0284c7",     // sky-600
    soft: "rgba(2,132,199,0.10)",
    Icon: CloudRain,
  },
  incoming: {
    label: "Incoming",
    color: "#d97706",     // amber-600
    soft: "rgba(217,119,6,0.10)",
    Icon: CloudFog,
  },
  dry: {
    label: "Dry",
    color: "#52525b",     // zinc-600
    soft: "rgba(82,82,91,0.06)",
    Icon: Sun,
  },
};

/** Stable departure-board sort:
 *   1) Tier (downpour > drizzling > incoming > dry)
 *   2) Within tier: intensity desc for raining, ETA asc for incoming, distance asc for dry
 *   3) Tiebreak by population desc (so the bigger hub wins)
 */
function sortHubs(hubs) {
  return [...hubs].sort((a, b) => {
    const ta = TIER_ORDER[a.status] ?? 9;
    const tb = TIER_ORDER[b.status] ?? 9;
    if (ta !== tb) return ta - tb;
    if (a.status === "incoming") {
      const ea = a.eta_minutes ?? 999;
      const eb = b.eta_minutes ?? 999;
      if (ea !== eb) return ea - eb;
    } else if (a.status === "dry") {
      if (a.distance_km !== b.distance_km) return a.distance_km - b.distance_km;
    } else {
      const ia = a.peak_intensity_mm_h ?? a.intensity_mm_h ?? 0;
      const ib = b.peak_intensity_mm_h ?? b.intensity_mm_h ?? 0;
      if (ia !== ib) return ib - ia;
    }
    return (b.population ?? 0) - (a.population ?? 0);
  });
}

function formatEta(min, intensityNow = 0) {
  if (min == null) return null;
  // Open-Meteo's `minutely_15` returns 15-minute buckets, so the smallest
  // ETA we can ever resolve is 15. When the rain is already detectable in
  // the very first bucket it might be falling NOW or up to 15 min away —
  // so we render a friendlier sub-bucket label rather than always saying
  // "15 min" (which made every hub look identical to users).
  if (intensityNow >= 0.05) return "now";
  if (min <= 15) return "<15 min";
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m === 0 ? `${h} h` : `${h} h ${m} m`;
}

function intensityBarPct(mm_h) {
  // Map mm/h to 0–100% on a perceptual log-ish scale: 0.5→20, 2→40, 7→70, 25→100
  if (!mm_h || mm_h <= 0.05) return 0;
  if (mm_h >= 25) return 100;
  if (mm_h >= 7) return 70 + ((mm_h - 7) / 18) * 30;
  if (mm_h >= 2) return 40 + ((mm_h - 2) / 5) * 30;
  return 20 + ((mm_h - 0.5) / 1.5) * 20;
}

function PulseCard({ hub, idx, theme, hideSplitBadge = false }) {
  const meta = STATUS_META[hub.status] || STATUS_META.dry;
  const Icon = meta.Icon;
  const intensity = hub.peak_intensity_mm_h || hub.intensity_mm_h || 0;
  const eta = formatEta(hub.eta_minutes, hub.intensity_mm_h || 0);
  const barPct = intensityBarPct(intensity);

  return (
    <motion.div
      data-testid={`pulse-card-${hub.name?.toLowerCase().replace(/\s+/g, "-")}`}
      className="rounded-2xl px-3.5 py-3 relative overflow-hidden"
      style={{
        background: hub.status === "downpour" ? meta.soft : theme.surface,
        border: `1px solid ${hub.status === "downpour" ? `${meta.color}55` : theme.surfaceBorder}`,
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
      }}
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.28, delay: idx * 0.03 }}
    >
      <span
        aria-hidden
        className="absolute left-0 top-0 bottom-0 w-[3px]"
        style={{ background: meta.color }}
      />

      <div className="flex items-start gap-3 ml-1">
        {/* Left: name + distance */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span
              className="text-[14.5px] font-extrabold tracking-tight truncate"
              style={{ color: theme.textPrimary, letterSpacing: "-0.02em" }}
            >
              {hub.name}
            </span>
            {hub.divergent && !hideSplitBadge && (
              <span
                className="inline-flex items-center gap-0.5 px-1.5 py-[1px] rounded-md text-[8.5px] font-extrabold uppercase tracking-wider"
                style={{
                  color: "#b45309",
                  background: "rgba(217,119,6,0.12)",
                  border: "1px solid rgba(217,119,6,0.35)",
                }}
                title="Forecast models disagree on this hub"
                data-testid="divergence-badge"
              >
                <Warning size={9} weight="fill" />
                Split
              </span>
            )}
          </div>
          <div
            className="text-[10px] mt-0.5 flex items-center gap-1 font-semibold"
            style={{ color: theme.textMuted }}
          >
            <MapPin size={9} weight="fill" />
            <span className="tabular-nums">{(hub.distance_km ?? 0).toFixed(1)} km</span>
            {hub.temperature_c != null && (
              <>
                <span className="opacity-60">·</span>
                <span className="tabular-nums">{Math.round(hub.temperature_c)}°</span>
              </>
            )}
          </div>

          {/* Intensity bar */}
          <div className="mt-2 h-[5px] rounded-full overflow-hidden" style={{ background: `${meta.color}1a` }}>
            <motion.div
              className="h-full rounded-full"
              style={{ background: meta.color }}
              initial={{ width: 0 }}
              animate={{ width: `${barPct}%` }}
              transition={{ duration: 0.6, ease: "easeOut" }}
            />
          </div>
        </div>

        {/* Right: status pill + ETA */}
        <div className="shrink-0 flex flex-col items-end gap-1">
          <span
            className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[9.5px] font-extrabold uppercase tracking-[0.14em]"
            style={{
              color: meta.color,
              background: meta.soft,
              border: `1px solid ${meta.color}40`,
            }}
            data-testid={`pulse-status-${hub.status}`}
          >
            <Icon size={10} weight="fill" />
            {meta.label}
          </span>
          {hub.status === "incoming" && eta && (
            <span
              className="inline-flex items-center gap-1 text-[10px] font-bold tabular-nums"
              style={{ color: meta.color }}
              data-testid={`pulse-eta-${hub.name?.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <Clock size={9} weight="fill" />
              {eta === "now" ? "Falling now" : `ETA ${eta}`}
            </span>
          )}
          {(hub.status === "downpour" || hub.status === "drizzling") && (
            <span
              className="text-[10px] font-extrabold tabular-nums"
              style={{ color: meta.color }}
            >
              {intensity.toFixed(1)} mm/h
            </span>
          )}
          {hub.status === "dry" && hub.max_prob_3h > 30 && (
            <span
              className="inline-flex items-center gap-0.5 text-[9.5px] font-bold tabular-nums"
              style={{ color: theme.textMuted }}
            >
              <ArrowDown size={9} weight="bold" />
              {hub.max_prob_3h}% · 3h
            </span>
          )}
        </div>
      </div>
    </motion.div>
  );
}

/**
 * CityPulseView — auto-derived neighbourhood rain dashboard.
 * Renders alongside the compass view inside NearbyRainPage via a top toggle.
 */
export default function CityPulseView({ location, theme, active }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!active || !location?.latitude || !location?.longitude) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    // Auto-expand radius if too few hubs come back. This rescues rural users
    // and bad GPS pings — most cities500 places are at 1k+ population, so a
    // user in a sparse area still gets useful coverage at 50–80 km.
    const attempts = [25, 50, 80];
    (async () => {
      try {
        let result = null;
        for (const r of attempts) {
          if (cancelled) return;
          const got = await getCityPulse(
            location.latitude,
            location.longitude,
            r,
            10,
          );
          result = got;
          if ((got?.hubs?.length || 0) >= 3) break;
        }
        if (!cancelled) setData(result);
      } catch (e) {
        if (!cancelled) setError(e?.message || "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [active, location?.latitude, location?.longitude]);

  const sortedHubs = useMemo(() => sortHubs(data?.hubs || []), [data]);
  const counts = useMemo(() => {
    const c = { downpour: 0, drizzling: 0, incoming: 0, dry: 0 };
    sortedHubs.forEach((h) => {
      if (c[h.status] !== undefined) c[h.status] += 1;
    });
    return c;
  }, [sortedHubs]);

  return (
    <div className="flex-1 min-h-0 flex flex-col" data-testid="city-pulse-view">
      <style>{`[data-testid="city-pulse-list"]::-webkit-scrollbar { display: none; width: 0; height: 0; }`}</style>
      {/* Sub-header — counts + sort hint */}
      <div className="px-1 mb-2 flex items-center gap-2 flex-wrap">
        <span
          className="text-[9.5px] uppercase tracking-[0.18em] font-extrabold"
          style={{ color: theme.textMuted }}
        >
          {data?.hubs?.length || 0} hubs · sorted by severity{data?.radius_km ? ` · ${Math.round(data.radius_km)} km` : ""}
        </span>
        <div className="flex-1" />
        {counts.downpour > 0 && (
          <Pill color={STATUS_META.downpour.color}>{counts.downpour} downpour</Pill>
        )}
        {counts.drizzling > 0 && (
          <Pill color={STATUS_META.drizzling.color}>{counts.drizzling} rain</Pill>
        )}
        {counts.incoming > 0 && (
          <Pill color={STATUS_META.incoming.color}>{counts.incoming} incoming</Pill>
        )}
      </div>

      {/* List — scrolls within the page bounds */}
      <div
        className="flex-1 min-h-0 overflow-y-auto space-y-2 px-1 pb-2"
        style={{
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          overscrollBehavior: "contain",
        }}
        data-testid="city-pulse-list"
      >
        {loading && !data && (
          <SkeletonList theme={theme} />
        )}
        {error && (
          <div
            className="rounded-2xl p-4 text-[12px] font-medium"
            style={{
              background: theme.surface,
              border: `1px solid ${theme.surfaceBorder}`,
              color: theme.textMuted,
            }}
          >
            Couldn&apos;t load pulse — {error}.
          </div>
        )}
        {data?.models_split?.all_split && (
          <ModelSplitBanner rollup={data.models_split} theme={theme} />
        )}
        {sortedHubs.map((hub, i) => (
          <PulseCard
            key={`${hub.name}-${hub.lat}-${hub.lon}`}
            hub={hub}
            idx={i}
            theme={theme}
            hideSplitBadge={Boolean(data?.models_split?.all_split)}
          />
        ))}
        {!loading && !error && sortedHubs.length === 0 && (
          <div
            className="rounded-2xl p-4 text-[12px] font-medium text-center"
            style={{
              background: theme.surface,
              border: `1px solid ${theme.surfaceBorder}`,
              color: theme.textMuted,
            }}
          >
            No populated hubs found within 25 km of this location.
          </div>
        )}
      </div>
    </div>
  );
}

function Pill({ color, children }) {
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-extrabold uppercase tracking-[0.14em]"
      style={{
        color,
        background: `${color}1a`,
        border: `1px solid ${color}40`,
      }}
    >
      {children}
    </span>
  );
}

/**
 * Rendered above the hub list when ≥70% of nearby hubs all share the same
 * model split. Replaces the spammy per-card "Split" badge with a single
 * honest summary so users understand the situation rather than seeing
 * 10 identical warning chips.
 */
function ModelSplitBanner({ rollup, theme }) {
  const models = rollup?.models || [];
  if (!models.length) return null;
  return (
    <div
      className="rounded-2xl px-3.5 py-3"
      data-testid="model-split-banner"
      style={{
        background: "rgba(217,119,6,0.08)",
        border: "1px solid rgba(217,119,6,0.30)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
      }}
    >
      <div className="flex items-center gap-1.5 mb-1.5">
        <Warning size={13} weight="fill" style={{ color: "#b45309" }} />
        <span
          className="text-[11px] font-extrabold uppercase tracking-wider"
          style={{ color: "#b45309" }}
        >
          Models split across this radius
        </span>
      </div>
      <p
        className="text-[11px] leading-relaxed"
        style={{ color: theme.textPrimary }}
      >
        {rollup.split_hubs}/{rollup.total_hubs} hubs see disagreement among the
        global forecast models for the next 3 hours:
      </p>
      <div className="flex flex-wrap gap-1.5 mt-2">
        {models.map((m) => (
          <span
            key={m.model}
            className="inline-flex items-center gap-1 px-2 py-[3px] rounded-md text-[10px] font-bold tabular-nums"
            style={{
              color: theme.textPrimary,
              background: theme.chip,
              border: `1px solid ${theme.surfaceBorder}`,
            }}
            data-testid={`model-split-chip-${m.model.toLowerCase()}`}
          >
            <span style={{ color: theme.textMuted, fontWeight: 700 }}>{m.model}</span>
            <span style={{ color: m.mm_3h >= 2 ? "#b45309" : theme.textPrimary }}>
              {m.mm_3h.toFixed(1)} mm
            </span>
          </span>
        ))}
      </div>
      <p
        className="text-[10px] mt-2 leading-relaxed"
        style={{ color: theme.textMuted }}
      >
        Take the per-hub forecasts as a range, not a number. Re-check in 30 min.
      </p>
    </div>
  );
}

function SkeletonList({ theme }) {
  return (
    <>
      {[0, 1, 2, 3, 4].map((i) => (
        <div
          key={i}
          className="rounded-2xl px-3.5 py-3"
          style={{
            background: theme.surface,
            border: `1px solid ${theme.surfaceBorder}`,
          }}
        >
          <div className="h-3 w-32 rounded animate-pulse" style={{ background: theme.surfaceBorder }} />
          <div className="h-2 w-20 mt-2 rounded animate-pulse" style={{ background: theme.surfaceBorder }} />
          <div className="h-1 w-full mt-3 rounded-full animate-pulse" style={{ background: theme.surfaceBorder }} />
        </div>
      ))}
    </>
  );
}
